import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// API URL resolution:
// - VITE_API_URL set: use it (dev or custom deployment)
// - HTTPS + domain: api.<domain> (e.g. domena.pl → api.domena.pl)
// - HTTP (direct IP): same host, port 3003
// - Dev (localhost): empty = Vite proxy
function resolveApiUrl(): string {
  const env = import.meta.env.VITE_API_URL;
  if (env) return env;
  const { protocol, hostname } = window.location;
  if (protocol === 'https:') return `https://api.${hostname}`;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '';
  return `http://${hostname}:3003`;
}
const API_URL = resolveApiUrl();

// Event emitter for auth events
type AuthEventCallback = () => void;
const authEventListeners: AuthEventCallback[] = [];

export const onUnauthorized = (callback: AuthEventCallback) => {
  authEventListeners.push(callback);
  return () => {
    const index = authEventListeners.indexOf(callback);
    if (index > -1) {
      authEventListeners.splice(index, 1);
    }
  };
};

const emitUnauthorized = () => {
  authEventListeners.forEach((callback) => callback());
};

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // Skip interceptor for refresh endpoint itself (prevents infinite loop)
    const isRefreshRequest = originalRequest.url?.includes('/auth/refresh');

    // If error is 401, not a refresh request, and we haven't retried yet
    if (error.response?.status === 401 && !isRefreshRequest && !originalRequest._retry) {
      // If already refreshing, queue the request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No refresh token, logout
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Notify AuthContext to handle logout
        emitUnauthorized();
        isRefreshing = false;
        return Promise.reject(error);
      }

      try {
        // Try to refresh the token
        const response = await apiClient.post('/admin/auth/refresh', {
          refreshToken,
        });

        const { accessToken } = response.data;
        localStorage.setItem('accessToken', accessToken);

        // Update the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        }

        processQueue(null, accessToken);

        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed, logout
        processQueue(refreshError as AxiosError, null);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        // Notify AuthContext to handle logout
        emitUnauthorized();
        isRefreshing = false;
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// Helper function to handle API errors
export const handleApiError = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      message?: string;
      error?: { message?: string; code?: string };
      errors?: Record<string, string[]>;
    }>;

    // Check for nested error.message (used by planService and others)
    if (axiosError.response?.data?.error?.message) {
      return axiosError.response.data.error.message;
    }

    // Check for direct message
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message;
    }

    // Check for validation errors
    if (axiosError.response?.data?.errors) {
      const errors = axiosError.response.data.errors;
      const firstError = Object.values(errors)[0];
      return firstError ? firstError[0] : 'Wystąpił błąd';
    }

    if (axiosError.message) {
      return axiosError.message;
    }
  }

  return 'Wystąpił nieoczekiwany błąd';
};

export default apiClient;
